import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  X,
  Sparkles,
  IndianRupee,
  Camera,
  Power,
  CheckCircle2,
} from "lucide-react";
import { PricingBreakdown } from "../../../../Data/imagePurchase.dto";
import {
  getApiErrorMessage,
  getPricingPreview,
} from "../../../../Redux/ApiCalls/Dashboard/ImagePurchaseAPI";
import PricingBreakdownCard from "./PricingBreakdownCard";

interface PhotoPaymentModalProps {
  isOpen: boolean;
  isActive: boolean;
  initialprice: number;
  projectId: number;
  onClose: () => void;
  onUpdate?: (price: number, isActive: boolean) => void;
  onSubmit: (price: number) => void;
}

const PREVIEW_DEBOUNCE_MS = 400;

const PhotoPaymentModal: React.FC<PhotoPaymentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isActive,
  onUpdate,
  initialprice,
  projectId,
}) => {
  const [price, setPrice] = useState(
    isActive && initialprice ? initialprice.toString() : ""
  );
  const [error, setError] = useState("");
  const [breakdown, setBreakdown] = useState<PricingBreakdown | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPrice(isActive && initialprice ? initialprice.toString() : "");
      setError("");
    }
  }, [isOpen, isActive, initialprice]);

  useEffect(() => {
    if (!isOpen) return;
    const typed = parseInt(price, 10);
    const per_image_price = !isNaN(typed) && typed > 0 ? typed : undefined;
    let cancelled = false;

    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError("");
      try {
        const data = await getPricingPreview({ project_id: projectId, per_image_price });
        if (!cancelled) setBreakdown(data);
      } catch (err) {
        if (!cancelled) {
          setPreviewError(getApiErrorMessage(err, "Could not load pricing breakdown."));
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, price, projectId]);

  const salesDisabled = breakdown?.pricing_policy === "disabled";

  const validateAndSubmit = () => {
    const value = parseInt(price, 10);
    if (isNaN(value) || value <= 0) {
      setError("Please enter a valid price greater than 0.");
      return;
    }
    setError("");
    onSubmit(value);
    setPrice("");
    onClose();
  };

  const priceNum = parseInt(price, 10);
  const canActivate = !salesDisabled && !isActive && !!price && priceNum > 0;
  const canUpdate = !salesDisabled && isActive && !!price && priceNum > 0;

  if (!isOpen) return null;

  return (
    <Overlay
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <ModalBox
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <ModalHero>
          <HeroGlow />
          <HeroTop>
            <HeroIcon>
              <Camera size={22} />
            </HeroIcon>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                Photo sales
              </HeroBadge>
              <ModalTitle>Photo payment</ModalTitle>
              <ModalSubtitle>
                {isActive
                  ? "Update price per photo or deactivate photo payments."
                  : "Set a price per photo to start accepting payments."}
              </ModalSubtitle>
            </HeroText>
            <CloseBtn
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Close"
            >
              <X size={18} />
            </CloseBtn>
          </HeroTop>
        </ModalHero>

        <ModalBody>
          {isActive && (
            <StatusChip>
              <StatusDot />
              Currently active at ₹{initialprice.toLocaleString("en-IN")}
            </StatusChip>
          )}

          <SectionLabel>
            <IndianRupee size={12} />
            Price per photo
          </SectionLabel>

          <Field>
            <Label htmlFor="photo-price">Amount (₹)</Label>
            <InputWrap>
              <IndianRupee size={16} color="#c94daa" />
              <Input
                id="photo-price"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={price}
                maxLength={9}
                disabled={salesDisabled}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (val.length <= 9) {
                    setPrice(val);
                    setError("");
                  }
                }}
                onInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const cleaned = e.currentTarget.value
                    .replace(/\D/g, "")
                    .slice(0, 9);
                  e.currentTarget.value = cleaned;
                  setPrice(cleaned);
                }}
                placeholder="Enter price in rupees"
              />
            </InputWrap>
            {error && <ErrorText role="alert">{error}</ErrorText>}
          </Field>

          <SectionLabel>Charges &amp; payout</SectionLabel>
          <PricingBreakdownCard
            breakdown={breakdown}
            loading={previewLoading}
            error={previewError}
          />
        </ModalBody>

        <ActionRow>
          <CancelBtn
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            Cancel
          </CancelBtn>

          {isActive ? (
            <>
              <SecondaryBtn
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdate?.(0, !isActive);
                }}
              >
                <Power size={15} />
                Deactivate
              </SecondaryBtn>
              <SubmitBtn
                type="button"
                disabled={!canUpdate}
                onClick={(e) => {
                  e.stopPropagation();
                  validateAndSubmit();
                }}
              >
                <CheckCircle2 size={16} />
                Update
              </SubmitBtn>
            </>
          ) : (
            <SubmitBtn
              type="button"
              disabled={!canActivate}
              onClick={(e) => {
                e.stopPropagation();
                validateAndSubmit();
              }}
            >
              <CheckCircle2 size={16} />
              Activate
            </SubmitBtn>
          )}
        </ActionRow>
      </ModalBox>
    </Overlay>
  );
};

export default PhotoPaymentModal;

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 9999;
  padding: 16px;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 460px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
`;

const ModalHero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
  flex-shrink: 0;
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${gradient};
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.25);
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
  min-height: 0;
`;

const StatusChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(49, 166, 106, 0.1);
  border: 1px solid rgba(49, 166, 106, 0.22);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #31a66a;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #31a66a;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.55);
`;

const InputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus-within {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
`;

const Input = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #171717;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
    font-size: 14px;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #e44862;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 24px 22px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const CancelBtn = styled.button`
  flex: 0 0 auto;
  padding: 0 18px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(228, 72, 98, 0.25);
  background: rgba(228, 72, 98, 0.06);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #e44862;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    background: rgba(228, 72, 98, 0.1);
  }
`;

const SubmitBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;
